import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Wrench, Plus, Search, Loader2, LogOut, LogIn, CheckCircle2, Eye, Camera, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { vigilanciaAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import usePermissions from '../../hooks/usePermissions';

const SUB = 'herramentales';
const ESTADO_BADGE = {
  creado: { label: 'Creado', cls: 'bg-amber-100 text-amber-700' },
  en_transito: { label: 'En tránsito', cls: 'bg-blue-100 text-blue-700' },
  regresado: { label: 'Regresado', cls: 'bg-emerald-100 text-emerald-700' },
  cerrado: { label: 'Cerrado', cls: 'bg-slate-200 text-slate-600' },
};
const hora = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '—');
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND}${u}`) : null);

const emptyForm = {
  descripcion: '', cantidad: 1, tipo_salida: 'externa',
  proveedor_destino: '', motivo: '', nucleo_origen: '', nucleo_destino: '',
  fecha_hora_esperada_salida: '', fecha_hora_esperada_regreso: '',
};

export default function Herramentales() {
  const { user } = useAuth();
  const { canWrite, canAccess } = usePermissions();
  const isGuardia = !!user?.is_guardia;
  const canFull = canWrite('vigilancia', SUB);
  const canCreate = canAccess('vigilancia', SUB);

  const [vales, setVales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState(isGuardia ? 'creado' : 'todos');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detailVale, setDetailVale] = useState(null);
  const [photoModal, setPhotoModal] = useState(null); // { vale, tipo }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      if (estado !== 'todos') params.estado = estado;
      const res = await vigilanciaAPI.getVales(params);
      setVales(res.data || []);
    } catch { toast.error('Error al cargar vales'); }
    finally { setLoading(false); }
  }, [estado, search]);

  useEffect(() => { const t = setTimeout(fetchAll, 250); return () => clearTimeout(t); }, [fetchAll]);

  const submitForm = async () => {
    if (!form.descripcion.trim()) { toast.error('La descripción es obligatoria'); return; }
    if (form.tipo_salida === 'externa' && !form.proveedor_destino.trim() && !form.motivo.trim()) { toast.error('Externa requiere proveedor/destino o motivo'); return; }
    if (form.tipo_salida === 'interna' && (!form.nucleo_origen.trim() || !form.nucleo_destino.trim())) { toast.error('Interna requiere núcleo origen y destino'); return; }
    setSaving(true);
    try {
      await vigilanciaAPI.createVale({ ...form, cantidad: parseInt(form.cantidad) || 1 });
      toast.success('Vale creado');
      setFormOpen(false); setForm(emptyForm); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear vale'); }
    finally { setSaving(false); }
  };

  const doAction = async (fn, id, okMsg) => {
    setBusyId(id);
    try { await fn(id); toast.success(okMsg); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setBusyId(null); }
  };

  const openDetail = async (id) => {
    setDetailVale({ loading: true });
    try { const res = await vigilanciaAPI.getVale(id); setDetailVale(res.data); }
    catch { setDetailVale(null); toast.error('No se pudo abrir el detalle'); }
  };

  const destino = (v) => v.tipo_salida === 'externa' ? (v.proveedor_destino || v.motivo || '—') : `${v.nucleo_origen} → ${v.nucleo_destino}`;

  const ValeActions = ({ v, big }) => (
    <div className={big ? 'space-y-2' : 'inline-flex gap-2 flex-nowrap whitespace-nowrap'} onClick={(e) => e.stopPropagation()}>
      {canFull && v.estado === 'creado' && (
        <Button size={big ? 'default' : 'sm'} className={big ? 'w-full h-12' : ''} variant="outline" onClick={() => setPhotoModal({ vale: v, tipo: 'salida' })} disabled={busyId === v.id} data-testid={`vale-salida-${v.folio}`}><LogOut className="w-4 h-4 mr-1" /> Registrar salida</Button>
      )}
      {canFull && v.estado === 'en_transito' && (
        <Button size={big ? 'default' : 'sm'} className={big ? 'w-full h-12' : ''} variant="outline" onClick={() => setPhotoModal({ vale: v, tipo: 'regreso' })} disabled={busyId === v.id} data-testid={`vale-regreso-${v.folio}`}><LogIn className="w-4 h-4 mr-1" /> Registrar regreso</Button>
      )}
      {canFull && v.estado === 'regresado' && (
        <Button size={big ? 'default' : 'sm'} className={big ? 'w-full h-12' : ''} onClick={() => doAction(vigilanciaAPI.cerrarVale, v.id, 'Vale cerrado')} disabled={busyId === v.id} data-testid={`vale-cerrar-${v.folio}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Cerrar vale</Button>
      )}
      <Button size={big ? 'default' : 'sm'} className={big ? 'w-full' : ''} variant="ghost" onClick={() => openDetail(v.id)} data-testid={`vale-ver-${v.folio}`}><Eye className="w-4 h-4 mr-1" /> Ver detalle</Button>
    </div>
  );

  const DuracionBadge = ({ v }) => (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" /> {v.duracion}</span>
  );

  const dialogs = (
    <>
      <PhotoDialog photoModal={photoModal} onClose={() => setPhotoModal(null)} onDone={fetchAll} />
      <ValeDetailDialog vale={detailVale} onClose={() => setDetailVale(null)} destino={destino} />
    </>
  );

  // ---------- GUARD (mobile-first) ----------
  if (isGuardia) {
    return (
      <Layout>
        <div data-testid="vale-guard-view" className="space-y-4 max-w-xl mx-auto">
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Caseta · Herramental</h1>
          <div className="flex gap-2">
            {['creado', 'en_transito', 'regresado', 'todos'].map((e) => (
              <button key={e} onClick={() => setEstado(e)}
                className={`flex-1 py-2 rounded-lg text-xs font-medium ${estado === e ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                data-testid={`vale-guard-filter-${e}`}>
                {e === 'todos' ? 'Todos' : ESTADO_BADGE[e].label}
              </button>
            ))}
          </div>
          {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div>
            : vales.length === 0 ? <p className="text-center text-slate-400 py-10">Sin vales.</p>
            : vales.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid={`vale-guard-card-${v.folio}`}>
                <div className="flex items-center justify-between">
                  <div><p className="font-semibold text-slate-900">{v.descripcion}</p><p className="text-xs text-slate-500">{v.folio} · x{v.cantidad} · {v.tipo_salida}</p></div>
                  <Badge className={ESTADO_BADGE[v.estado]?.cls}>{ESTADO_BADGE[v.estado]?.label}</Badge>
                </div>
                <p className="text-sm text-slate-600">Destino: {destino(v)}</p>
                <DuracionBadge v={v} />
                <ValeActions v={v} big />
              </div>
            ))}
        </div>
        {dialogs}
      </Layout>
    );
  }

  // ---------- OFFICE ----------
  return (
    <Layout>
      <div data-testid="vale-office-view" className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-slate-900 flex items-center justify-center"><Wrench className="w-6 h-6 text-white" /></div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">Herramentales</h1>
              <p className="text-slate-500 text-sm">Vigilancia {!canFull && '· Mis vales'}</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={() => { setForm(emptyForm); setFormOpen(true); }} data-testid="vale-new-btn"><Plus className="w-4 h-4 mr-2" /> Crear vale</Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Buscar por descripción o folio…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vale-search" />
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
            {['todos', 'creado', 'en_transito', 'regresado', 'cerrado'].map((e) => (
              <button key={e} onClick={() => setEstado(e)}
                className={`px-3 py-1.5 text-sm rounded-md ${estado === e ? 'bg-white shadow text-slate-900 font-medium' : 'text-slate-500'}`}
                data-testid={`vale-filter-${e}`}>
                {e === 'todos' ? 'Todos' : ESTADO_BADGE[e].label}
              </button>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
          <table className="w-full min-w-[1000px] text-sm" data-testid="vale-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Folio</th>
                <th className="px-4 py-3 font-medium">Descripción</th>
                <th className="px-4 py-3 font-medium">Tipo / Destino</th>
                <th className="px-4 py-3 font-medium">Creado por</th>
                <th className="px-4 py-3 font-medium">Salida</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Duración</th>
                <th className="px-4 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : vales.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="vale-empty">Sin vales para mostrar.</td></tr>
              ) : vales.map((v) => (
                <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(v.id)} data-testid={`vale-row-${v.folio}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{v.folio}</td>
                  <td className="px-4 py-3"><span className="font-medium text-slate-800">{v.descripcion}</span><span className="block text-xs text-slate-400">x{v.cantidad}</span></td>
                  <td className="px-4 py-3 text-slate-600"><span className="capitalize">{v.tipo_salida}</span><span className="block text-xs text-slate-400">{destino(v)}</span></td>
                  <td className="px-4 py-3 text-slate-600">{v.creado_por_nombre || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{hora(v.hora_real_salida)}</td>
                  <td className="px-4 py-3"><Badge className={ESTADO_BADGE[v.estado]?.cls}>{ESTADO_BADGE[v.estado]?.label}</Badge></td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{v.duracion}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap"><ValeActions v={v} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vale-form-dialog">
          <DialogHeader><DialogTitle>Crear vale de herramental</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-600">Descripción *</Label><Input value={form.descripcion} onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))} data-testid="vale-field-descripcion" /></div>
            <div><Label className="text-xs text-slate-600">Cantidad *</Label><Input type="number" min={1} value={form.cantidad} onChange={(e) => setForm((p) => ({ ...p, cantidad: e.target.value }))} data-testid="vale-field-cantidad" /></div>
            <div>
              <Label className="text-xs text-slate-600">Tipo de salida</Label>
              <Select value={form.tipo_salida} onValueChange={(v) => setForm((p) => ({ ...p, tipo_salida: v }))}>
                <SelectTrigger data-testid="vale-field-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="externa">Externa</SelectItem><SelectItem value="interna">Interna</SelectItem></SelectContent>
              </Select>
            </div>
            {form.tipo_salida === 'externa' ? (
              <>
                <div><Label className="text-xs text-slate-600">Proveedor / destino</Label><Input value={form.proveedor_destino} onChange={(e) => setForm((p) => ({ ...p, proveedor_destino: e.target.value }))} data-testid="vale-field-proveedor" /></div>
                <div><Label className="text-xs text-slate-600">Motivo</Label><Input value={form.motivo} onChange={(e) => setForm((p) => ({ ...p, motivo: e.target.value }))} data-testid="vale-field-motivo" /></div>
              </>
            ) : (
              <>
                <div><Label className="text-xs text-slate-600">Núcleo origen *</Label><Input value={form.nucleo_origen} onChange={(e) => setForm((p) => ({ ...p, nucleo_origen: e.target.value }))} data-testid="vale-field-origen" /></div>
                <div><Label className="text-xs text-slate-600">Núcleo destino *</Label><Input value={form.nucleo_destino} onChange={(e) => setForm((p) => ({ ...p, nucleo_destino: e.target.value }))} data-testid="vale-field-destino" /></div>
              </>
            )}
            <div><Label className="text-xs text-slate-600">Salida esperada</Label><Input type="datetime-local" value={form.fecha_hora_esperada_salida} onChange={(e) => setForm((p) => ({ ...p, fecha_hora_esperada_salida: e.target.value }))} data-testid="vale-field-fsalida" /></div>
            <div><Label className="text-xs text-slate-600">Regreso esperado</Label><Input type="datetime-local" value={form.fecha_hora_esperada_regreso} onChange={(e) => setForm((p) => ({ ...p, fecha_hora_esperada_regreso: e.target.value }))} data-testid="vale-field-fregreso" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={submitForm} disabled={saving} data-testid="vale-form-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Crear vale</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dialogs}
    </Layout>
  );
}

function PhotoDialog({ photoModal, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setFile(null); setPreview(null);
  }, [photoModal]);

  if (!photoModal) return null;
  const { vale, tipo } = photoModal;
  const esSalida = tipo === 'salida';

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const confirm = async () => {
    if (esSalida && !file) { toast.error('Debes tomar una foto para registrar la salida'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (esSalida) await vigilanciaAPI.registrarSalidaVale(vale.id, fd);
      else await vigilanciaAPI.registrarRegresoVale(vale.id, fd);
      toast.success(esSalida ? 'Salida registrada' : 'Regreso registrado');
      onClose(); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al registrar'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!photoModal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vale-photo-dialog">
        <DialogHeader>
          <DialogTitle>{esSalida ? 'Foto de salida (obligatoria)' : 'Foto de regreso (opcional)'}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{vale.descripcion} · {vale.folio}</p>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="vale-photo-input" />
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-slate-300 rounded-xl h-56 flex items-center justify-center cursor-pointer bg-slate-50 overflow-hidden"
          data-testid="vale-photo-dropzone"
        >
          {preview ? (
            <img src={preview} alt="preview" className="h-full w-full object-contain" />
          ) : (
            <div className="text-center text-slate-400">
              <Camera className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">Toca para tomar/seleccionar foto</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button onClick={confirm} disabled={busy || (esSalida && !file)} data-testid="vale-photo-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {esSalida ? 'Registrar salida' : 'Registrar regreso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-sm text-slate-800">{value === null || value === undefined || value === '' ? '—' : value}</span>
  </div>
);

function ValeDetailDialog({ vale, onClose, destino }) {
  if (!vale) return null;
  const loading = vale.loading;
  const fSalida = fullUrl(vale.foto_salida_url);
  const fRegreso = fullUrl(vale.foto_regreso_url);
  return (
    <Dialog open={!!vale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vale-detail-dialog">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap">
                <span>{vale.descripcion}</span>
                <Badge className={ESTADO_BADGE[vale.estado]?.cls}>{ESTADO_BADGE[vale.estado]?.label}</Badge>
                <span className="text-xs text-slate-400 font-normal font-mono">{vale.folio}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="rounded-lg bg-slate-900 text-white px-4 py-3 flex items-center gap-2" data-testid="vale-detail-duracion">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{vale.duracion}</span>
            </div>

            {(fSalida || fRegreso) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Foto salida</p>
                  {fSalida ? <img src={fSalida} alt="salida" className="rounded-lg border border-slate-200 w-full h-40 object-cover" data-testid="vale-foto-salida" /> : <div className="h-40 rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400">Sin foto</div>}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Foto regreso</p>
                  {fRegreso ? <img src={fRegreso} alt="regreso" className="rounded-lg border border-slate-200 w-full h-40 object-cover" data-testid="vale-foto-regreso" /> : <div className="h-40 rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400">Sin foto</div>}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <DetailRow label="Cantidad" value={vale.cantidad} />
              <DetailRow label="Tipo de salida" value={vale.tipo_salida} />
              <DetailRow label="Destino" value={destino(vale)} />
              <DetailRow label="Creado por" value={vale.creado_por_nombre} />
              <DetailRow label="Salida esperada" value={vale.fecha_hora_esperada_salida ? hora(vale.fecha_hora_esperada_salida) : null} />
              <DetailRow label="Regreso esperado" value={vale.fecha_hora_esperada_regreso ? hora(vale.fecha_hora_esperada_regreso) : null} />
              <DetailRow label="Salida real" value={hora(vale.hora_real_salida)} />
              <DetailRow label="Regreso real" value={hora(vale.hora_real_regreso)} />
              {vale.motivo && <DetailRow label="Motivo" value={vale.motivo} />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
